/* Usuários (Painel Administrativo) — gestão de acesso ao painel.
 *
 * Ligada ao real `/api/users` (Fase 0): listar, criar (com senha temporária
 * devolvida uma vez), ativar/desativar e redefinir senha. Substitui o CLI.
 * Nebula. */

import React, { useState } from 'react';

import { api } from '../api/cliente';
import { useRecurso } from '../estado/recurso';
import { relativo } from './formato';
import { Aviso, Button, card, Cabecalho, Icon, IconButton, Nota, Secao, Select, Tag } from './ui';

const PAPEL_ROTULO = { admin: 'Plataforma', gestor: 'Gestor', atendente: 'Atendente' };

const th = {
  position: 'sticky', top: 0, textAlign: 'left', padding: '12px 20px',
  background: 'var(--surface-card)', font: 'var(--type-label)', fontWeight: 'var(--fw-semibold)',
  color: 'var(--text-muted)', letterSpacing: 'var(--ls-wide)', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap',
};
const td = { padding: '12px 20px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' };

export default function Usuarios() {
  const recurso = useRecurso(
    (sinal) => Promise.all([api.usuarios(sinal), api.empresas(sinal)]).then(([u, e]) => ({ usuarios: u?.itens || [], empresas: e?.itens || [] })),
    [],
  );
  const usuarios = recurso.dados?.usuarios || [];
  const empresas = recurso.dados?.empresas || [];
  const [criando, setCriando] = useState(false);
  const [senhaRevelada, setSenhaRevelada] = useState(null);

  async function alternarAtivo(u) {
    await api.salvarUsuario(u.user_id, { ativo: !u.ativo });
    recurso.recarregar();
  }

  async function redefinir(u) {
    const r = await api.redefinirSenha(u.user_id);
    setSenhaRevelada({ email: u.email, senha: r.senha_temporaria });
  }

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Cabecalho
        titulo="Usuários"
        descricao="Quem acessa o painel — da plataforma e das empresas. Você cria, desativa e redefine a senha."
        acoes={<Button variant="primary" icon="plus" onClick={() => setCriando(true)}>Adicionar usuário</Button>}
      />

      {criando && <CriarUsuario empresas={empresas} aoFechar={() => setCriando(false)} aoCriado={(res) => { recurso.recarregar(); if (res?.senha_temporaria) setSenhaRevelada({ email: res.email, senha: res.senha_temporaria }); }} />}
      {senhaRevelada && <SenhaRevelada dados={senhaRevelada} aoFechar={() => setSenhaRevelada(null)} />}

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 24px 8px' }}>
        <Secao titulo={recurso.carregando ? 'Carregando…' : `${usuarios.length} ${usuarios.length === 1 ? 'usuário' : 'usuários'}`}>
          {recurso.erro ? (
            <Aviso titulo="Os usuários não carregaram" texto={recurso.erro.mensagem} acao={recurso.erro.temporario ? 'Tentar de novo' : null} aoAcao={recurso.recarregar} />
          ) : !recurso.carregando && usuarios.length === 0 ? (
            <Aviso titulo="Nenhum usuário" texto="Adicione o primeiro acesso ao painel." acao="Adicionar usuário" aoAcao={() => setCriando(true)} />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)' }}>
              <thead>
                <tr>
                  <th style={th}>Usuário</th><th style={th}>Papel</th><th style={th}>Acesso</th>
                  <th style={{ ...th, textAlign: 'right' }}>Último acesso</th><th style={th}>Estado</th><th style={{ ...th, textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => (
                  <tr key={u.user_id}>
                    <td style={td}>
                      <span style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>{u.nome}</span>
                        <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{u.email}</span>
                      </span>
                    </td>
                    <td style={td}><Tag tone={u.papel === 'admin' ? 'info' : 'neutral'}>{PAPEL_ROTULO[u.papel] || u.papel}</Tag></td>
                    <td style={td}>{u.todos_os_tenants ? 'Todas as empresas' : `${(u.tenants || []).length} empresa(s)`}</td>
                    <td className="nb-tab" style={{ ...td, textAlign: 'right', color: 'var(--text-muted)' }}>{u.ultimo_acesso_em ? relativo(u.ultimo_acesso_em) : 'nunca'}</td>
                    <td style={td}>
                      <button type="button" onClick={() => alternarAtivo(u)} title="Ativar/desativar" style={{ cursor: 'pointer', border: 0, background: 'transparent', padding: 0 }}>
                        <Tag tone={u.ativo ? 'success' : 'danger'} dot={u.ativo}>{u.ativo ? 'Ativo' : 'Desligado'}</Tag>
                      </button>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <Button variant="secondary" icon="key-round" style={{ height: 30, padding: '0 10px', fontSize: 'var(--fs-label)' }} onClick={() => redefinir(u)}>Redefinir senha</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Secao>
        <Nota>Fonte: /api/users. Desativar derruba as sessões abertas na hora. A senha é sempre temporária e aparece uma única vez.</Nota>
      </div>
    </div>
  );
}

const rot = { font: 'var(--type-label)', color: 'var(--text-secondary)' };
const inp = { height: 40, padding: '0 12px', width: '100%', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-control)', color: 'var(--text-primary)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', outline: 'none' };

function CriarUsuario({ empresas, aoFechar, aoCriado }) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState('gestor');
  const [plataforma, setPlataforma] = useState(false);
  const [sender, setSender] = useState(empresas[0]?.sender || '');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);

  const valido = nome.trim() && email.trim() && (plataforma || sender);

  async function enviar() {
    setEnviando(true);
    setErro(null);
    try {
      const res = await api.criarUsuario({
        nome: nome.trim(), email: email.trim(), papel,
        todos_os_tenants: plataforma,
        senders: plataforma ? [] : [sender],
      });
      aoCriado?.(res);
      aoFechar();
    } catch (e) {
      setErro(e.codigo === 'email_em_uso' ? 'Já existe um acesso com este e-mail.' : (e.mensagem || 'Não foi possível criar.'));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(4,5,20,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 6 }}>
      <div style={{ ...card, width: 480, maxWidth: '100%', boxShadow: 'var(--shadow-md)' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ margin: 0, font: 'var(--type-h3)' }}>Adicionar usuário</h3>
          <IconButton icon="x" label="Fechar" onClick={aoFechar} />
        </header>
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><span style={rot}>Nome</span><input style={inp} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ana Ribeiro" /></label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><span style={rot}>E-mail</span><input type="email" style={inp} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ana@empresa.org" /></label>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={rot}>Papel</span>
            <Select value={papel} onChange={(e) => setPapel(e.target.value)} fullWidth options={[
              { value: 'gestor', label: 'Gestor (métricas e conversas da empresa)' },
              { value: 'atendente', label: 'Atendente (só conversas)' },
              { value: 'admin', label: 'Plataforma (administra tudo)' },
            ]} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={plataforma} onChange={(e) => setPlataforma(e.target.checked)} />
            <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>Acesso a todas as empresas (conta de plataforma)</span>
          </label>
          {!plataforma && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={rot}>Empresa</span>
              <Select value={sender} onChange={(e) => setSender(e.target.value)} fullWidth options={empresas.map((emp) => ({ value: emp.sender, label: emp.nome }))} />
            </label>
          )}
          {erro && <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--danger-text)' }}>{erro}</span>}
        </div>
        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
          <Button variant="ghost" onClick={aoFechar}>Cancelar</Button>
          <Button variant="primary" onClick={enviar} disabled={!valido || enviando} style={!valido || enviando ? { opacity: 0.6, cursor: 'default' } : undefined}>{enviando ? 'Criando…' : 'Criar usuário'}</Button>
        </footer>
      </div>
    </div>
  );
}

function SenhaRevelada({ dados, aoFechar }) {
  const [copiado, setCopiado] = useState(false);
  async function copiar() {
    try { await navigator.clipboard.writeText(dados.senha); setCopiado(true); setTimeout(() => setCopiado(false), 1500); } catch { /* sem clipboard */ }
  }
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(4,5,20,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 7 }}>
      <div style={{ ...card, width: 440, maxWidth: '100%', boxShadow: 'var(--shadow-md)' }}>
        <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 20px 8px', color: 'var(--success-text)' }}>
          <Icon name="circle-check" size={20} />
          <span style={{ font: 'var(--type-title)' }}>Senha temporária</span>
        </header>
        <div style={{ padding: '4px 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ margin: 0, font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>
            Envie para <b>{dados.email}</b>. Ela aparece <b>só agora</b> e substitui a anterior.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 14, background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-control)' }}>
            <code style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-body)', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{dados.senha}</code>
            <Button variant="secondary" icon={copiado ? 'check' : 'key-round'} onClick={copiar} style={{ height: 34, padding: '0 12px' }}>{copiado ? 'Copiado' : 'Copiar'}</Button>
          </div>
        </div>
        <footer style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 20px 16px' }}>
          <Button variant="primary" onClick={aoFechar}>Concluir</Button>
        </footer>
      </div>
    </div>
  );
}
